import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import FAQ from 'components/ui/FAQ';
import RelatedBlogs from 'components/RelatedBlogs';
import { makeAutomationFAQs } from '../data/faqs';
import { useNavigate } from 'react-router-dom';

const MakeAutomation = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'Make (Integromat) Automation Services - Advanced Workflow Automation | OctalChip',
    description: 'Professional Make automation services. We design complex automation scenarios using Make for businesses that require advanced logic, branching, and data processing.',
    keywords: 'make automation, integromat automation, make workflow, make integration, workflow automation, business automation, make services, automation consulting, advanced automation',
    canonical: 'https://www.octalchip.com/services/make-automation',
    faqData: makeAutomationFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Make (Integromat) Automation Services",
      "description": "Advanced workflow automation using Make for complex business process automation with advanced logic and data processing",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const makeCapabilities = [
    {
      title: 'Visual scenario design',
      description: 'Create complex automation scenarios with intuitive visual workflow builder',
      icon: 'LayoutTemplate',
      accent: 'top'
    },
    {
      title: 'Routers and filters',
      description: 'Implement advanced routing logic and data filtering for conditional workflows',
      icon: 'Settings',
      accent: 'right'
    },
    {
      title: 'Data transformation',
      description: 'Transform and manipulate data between modules with powerful data mapping tools',
      icon: 'RefreshCw',
      accent: 'bottom'
    },
    {
      title: 'API modules',
      description: 'Connect with REST APIs, webhooks, and custom integrations for seamless data flow',
      icon: 'Link',
      accent: 'left'
    },
    {
      title: 'High-volume automation',
      description: 'Handle large-scale automation with efficient processing and error handling',
      icon: 'TrendingUp',
      accent: 'top'
    }
  ];

  const useCases = [
    {
      title: 'Multi-app data pipelines',
      description: 'Build complex data pipelines that sync data across multiple applications and systems',
      icon: 'Database',
      color: 'bg-[#00d690]'
    },
    {
      title: 'Reporting & analytics workflows',
      description: 'Automate data collection, aggregation, and report generation for business intelligence',
      icon: 'BarChart',
      color: 'bg-[#00b876]'
    },
    {
      title: 'Marketing automation flows',
      description: 'Create sophisticated marketing automation with segmentation, personalization, and triggers',
      icon: 'Megaphone',
      color: 'bg-[#00d690]'
    },
    {
      title: 'SaaS backend orchestration',
      description: 'Orchestrate complex backend processes with conditional logic and error handling',
      icon: 'Server',
      color: 'bg-[#00b876]'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section - Asymmetric with Geometric Shapes */}
      <section id="hero" className="relative py-12 md:py-20 lg:py-28 overflow-hidden">
        {/* Complex Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#00d690]/5 via-white to-[#00b876]/5"></div>
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#00d690]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-[#00b876]/10 rounded-full blur-3xl"></div>
        </div>
        
        {/* Geometric Shapes */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-[#00d690]/10 rounded-2xl transform rotate-45 hidden lg:block"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-[#00b876]/10 rounded-full hidden lg:block"></div>
        <div className="absolute top-1/2 right-1/3 w-16 h-16 bg-[#00d690]/5 rounded-lg transform rotate-12 hidden lg:block"></div>

        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left - Visual */}
              <div className="relative hidden md:block">
                <div className="relative w-full aspect-square">
                  {/* Layered Geometric Design */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-3xl transform rotate-45"></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-36 h-36 bg-gradient-to-br from-[#00b876]/30 to-[#00d690]/30 rounded-2xl transform -rotate-12"></div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="w-28 h-28 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-xl shadow-2xl flex items-center justify-center transform rotate-6">
                      <Icon name="Settings" size={56} className="text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="space-y-6 md:space-y-8">
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-[#00d690]/10 rounded-full border border-[#00d690]/20">
                  <div className="w-2 h-2 bg-[#00d690] rounded-full animate-pulse"></div>
                  <span className="text-sm font-semibold text-[#00d690]">Advanced Automation</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gray-800">Advanced Workflow</span>
                  <br />
                  <span className="bg-gradient-to-r from-[#00d690] to-[#00b876] bg-clip-text text-transparent">
                    Automation with Make
                  </span>
                </h1>
                
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                  We design complex automation scenarios using Make for businesses that require advanced logic, 
                  branching, and data processing.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <Button 
                    variant="primary"
                    animation="scale"
                    onClick={() => navigate('/contact#contact-form')}
                    className="px-8 py-4 text-base shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                  >
                    Build Automation with Make
                  </Button>
                  <Button 
                    variant="outline"
                    animation="glow"
                    onClick={() => navigate('/services/workflow-automation#what-we-offer')}
                    className="px-8 py-4 text-base border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                  >
                    View All Automation Services
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Make Capabilities Section - Corner Accent Cards */}
      <section id="make-capabilities" className="py-16 md:py-24 relative">
        <div className="absolute inset-0 bg-gray-50"></div>
        <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-br from-white to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Make Capabilities
            </h2>
            <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful features for building advanced automation workflows
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
            {makeCapabilities.map((capability, index) => (
              <div 
                key={index} 
                className="group relative bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-[#00d690]/30 overflow-hidden"
              >
                {/* Corner Accent */}
                <div className={`absolute ${
                  capability.accent === 'top' ? 'top-0 right-0' :
                  capability.accent === 'right' ? 'top-0 right-0' :
                  capability.accent === 'bottom' ? 'bottom-0 left-0' :
                  'top-0 left-0'
                } w-24 h-24 bg-gradient-to-br from-[#00d690] to-[#00b876] opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                  capability.accent === 'top' ? 'rounded-bl-3xl' :
                  capability.accent === 'right' ? 'rounded-bl-3xl' :
                  capability.accent === 'bottom' ? 'rounded-tr-3xl' :
                  'rounded-br-3xl'
                }`}></div>
                
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-[#00d690]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#00d690] transition-all duration-300">
                    <Icon name={capability.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">
                    {capability.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {capability.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section - Colored Cards */}
      <section id="use-cases" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-white"></div>
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-50 to-transparent transform -skew-y-1 origin-top"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Make Use Cases
            </h2>
            <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Complex automation scenarios we build with Make
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                {/* Colored Background */}
                <div className={`absolute inset-0 ${useCase.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                
                {/* Content */}
                <div className="relative bg-white p-6 md:p-8 group-hover:bg-transparent transition-colors duration-300">
                  <div className={`w-14 h-14 ${useCase.color} rounded-xl flex items-center justify-center mb-4 shadow-lg transform group-hover:scale-110 transition-transform`}>
                    <Icon name={useCase.icon} size={28} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-white transition-colors">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-white/90 transition-colors leading-relaxed">
                    {useCase.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Geometric Design */}
      <section id="cta" className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white to-transparent transform -skew-y-1 origin-top"></div>
        
        {/* Background Shapes */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#00d690]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-[#00b876]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Decorative Geometric Elements */}
              <div className="absolute -top-8 -left-8 w-24 h-24 bg-[#00d690]/10 rounded-2xl transform rotate-45 hidden md:block"></div>
              <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-[#00b876]/10 rounded-full hidden md:block"></div>
              
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-[#00d690]/20 relative overflow-hidden">
                {/* Inner Pattern */}
                <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-[#00d690]/5 to-transparent rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#00b876]/5 to-transparent rounded-tr-full"></div>
                
                <div className="relative z-10 text-center">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                    Ready to Build Automation with Make?
                  </h2>
                  <div className="inline-block w-24 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mb-6"></div>
                  <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                    Let's design complex automation workflows with advanced logic and data processing. 
                    Get started with Make automation today.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      variant="primary"
                      animation="glow"
                      onClick={() => navigate('/contact#contact-form')}
                      className="bg-gradient-to-r from-[#00d690] to-[#00b876] hover:from-[#00b876] hover:to-[#00d690] text-white font-bold py-4 px-8 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    >
                      Build Automation with Make
                    </Button>
                    <Button
                      variant="outline"
                      animation="bounce"
                      onClick={() => navigate('/services/workflow-automation#what-we-offer')}
                      className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-4 px-8 transition-all duration-300"
                    >
                      View All Automation Services
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={makeAutomationFAQs}
        title="Make Automation FAQs"
        description="Common questions about Make (Integromat) automation, advanced workflows, and data transformation."
      />

      {/* Related Blogs Section */}
      <RelatedBlogs
        servicePath="/services/make-automation"
        serviceTags={['Make', 'Make Automation', 'Integromat', 'Workflow Automation', 'Automation']}
        serviceName="Make Automation"
        limit={6}
        title="Make Automation Related Blogs"
      />

      <Footer />
    </div>
  );
};

export default MakeAutomation;
